import { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

// Type definitions for hours structure
interface ServiceHours {
  open?: Record<string, string>;   // { monday: "09:00", ... }
  close?: Record<string, string>;  // { monday: "13:30", ... }
  open2?: Record<string, string>;  // Second window open times
  close2?: Record<string, string>; // Second window close times
}

interface HoursJson {
  delivery?: ServiceHours;
  takeout?: ServiceHours;
  business?: ServiceHours;
}

interface DateTimeSelectorProps {
  onTimeSelect: (date: Date | null, isAsap: boolean) => void;
  className?: string;
  isRestaurantOpen?: boolean; // undefined = loading, false = closed, true = open
  hoursJson?: HoursJson | null; // Restaurant operating hours
  timezone?: string | null; // Restaurant timezone (e.g., "America/Denver")
  serviceType?: 'delivery' | 'takeout'; // Current service type selection
  isAsapLoading?: boolean; // Whether ASAP availability is still loading
}

// Helper: Parse time string "HH:MM" to minutes since midnight
function parseTimeToMinutes(timeStr: string | undefined): number | null {
  if (!timeStr) return null;
  const parts = timeStr.split(':');
  if (parts.length !== 2) return null;
  const [hours, mins] = parts.map(Number);
  if (isNaN(hours) || isNaN(mins)) return null;
  return hours * 60 + mins;
}

// Helper: Get time windows for a specific service type and day
function getTimeWindows(serviceHours: ServiceHours | undefined, dayName: string): Array<{ open: number; close: number }> {
  if (!serviceHours) return [];
  
  const windows: Array<{ open: number; close: number }> = [];
  
  // First window
  const open1 = parseTimeToMinutes(serviceHours.open?.[dayName]);
  const close1 = parseTimeToMinutes(serviceHours.close?.[dayName]);
  if (open1 !== null && close1 !== null) {
    windows.push({ open: open1, close: close1 });
  }
  
  // Second window
  const open2 = parseTimeToMinutes(serviceHours.open2?.[dayName]);
  const close2 = parseTimeToMinutes(serviceHours.close2?.[dayName]);
  if (open2 !== null && close2 !== null) {
    windows.push({ open: open2, close: close2 });
  }
  
  return windows.sort((a, b) => a.open - b.open);
}

// Helper: Check if a time slot falls within operating windows
function isTimeInOperatingHours(minutes: number, windows: Array<{ open: number; close: number }>): boolean {
  return windows.some(w => minutes >= w.open && minutes < w.close);
}

export default function DateTimeSelector({ 
  onTimeSelect, 
  className, 
  isRestaurantOpen,
  hoursJson,
  timezone,
  serviceType = 'delivery',
  isAsapLoading = false
}: DateTimeSelectorProps) {
  // Treat undefined as loading state, don't default to open
  // Also consider ASAP loading state from parent
  const isLoading = isRestaurantOpen === undefined || isAsapLoading;
  const isOpen = isRestaurantOpen === true;
  
  const dayNames = useMemo(() => ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'], []);
  
  // Generate dates for today + 6 days
  const generateDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    
    return dates;
  };

  const dates = generateDates();
  
  const [selectedDate, setSelectedDate] = useState<Date | null>(dates[0]);
  const [selectedTime, setSelectedTime] = useState<string>(isOpen ? 'asap' : '');
  const initializedRef = useRef(false);
  const hasNotifiedParentRef = useRef(false);

  // Notify parent of default selection on mount (once)
  // If restaurant is closed or loading, don't auto-select ASAP - user must pick a scheduled time
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      if (isOpen) {
        onTimeSelect(dates[0], true);
        hasNotifiedParentRef.current = true;
      }
      // If closed or loading, don't notify parent until user selects a scheduled time
    }
  }, [onTimeSelect, isOpen]);
  
  // When loading finishes and restaurant is open, auto-select ASAP if not already notified
  useEffect(() => {
    if (!isLoading && isOpen && !hasNotifiedParentRef.current) {
      setSelectedTime('asap');
      onTimeSelect(dates[0], true);
      hasNotifiedParentRef.current = true;
    }
  }, [isLoading, isOpen, onTimeSelect, dates]);

  const formatDayLabel = (date: Date, index: number) => {
    if (index === 0) return 'TODAY';
    if (index === 1) return 'TMR';
    
    const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
    return days[date.getDay()];
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    // Only auto-select ASAP if restaurant is open (not loading, not closed)
    if (isOpen) {
      setSelectedTime('asap');
      onTimeSelect(date, true);
    } else {
      // When closed or loading, reset time selection - user must pick a scheduled time
      setSelectedTime('');
    }
  };

  // Helper: Convert a local time in restaurant timezone to UTC timestamp
  // Uses iterative adjustment with full date comparison for reliability
  const restaurantLocalToUtc = (dayIndex: number, hour: number, minute: number, tz: string): Date => {
    // Get restaurant's "today" using formatToParts
    const now = new Date();
    const todayFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });
    const todayParts = todayFormatter.formatToParts(now);
    const getPart = (p: Intl.DateTimeFormatPart[], type: string) => 
      parseInt(p.find(x => x.type === type)?.value || '0');
    
    // Restaurant's "today" 
    const rYear = getPart(todayParts, 'year');
    const rMonth = getPart(todayParts, 'month') - 1; // 0-indexed
    const rDay = getPart(todayParts, 'day');
    
    // Calculate target date using Date.UTC for proper overflow handling
    const targetDateBase = new Date(Date.UTC(rYear, rMonth, rDay + dayIndex, hour, minute, 0, 0));
    const tYear = targetDateBase.getUTCFullYear();
    const tMonth = targetDateBase.getUTCMonth() + 1; // 1-indexed for comparison
    const tDay = targetDateBase.getUTCDate();
    const tHour = hour;
    const tMinute = minute;
    
    // Formatter to check what local time a UTC timestamp represents
    const checkFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    });
    
    // Helper: check if a UTC timestamp's local representation matches our target
    const matchesTarget = (utcMs: number): { matches: boolean; diffMs: number } => {
      const p = checkFormatter.formatToParts(new Date(utcMs));
      const y = getPart(p, 'year');
      const m = getPart(p, 'month');
      const d = getPart(p, 'day');
      const h = getPart(p, 'hour');
      const min = getPart(p, 'minute');
      
      if (y === tYear && m === tMonth && d === tDay && h === tHour && min === tMinute) {
        return { matches: true, diffMs: 0 };
      }
      
      // Calculate how far off we are using actual Date objects for accuracy
      // Create a date representing what we got vs what we want
      const gotDate = new Date(Date.UTC(y, m - 1, d, h, min, 0, 0));
      const wantDate = new Date(Date.UTC(tYear, tMonth - 1, tDay, tHour, tMinute, 0, 0));
      
      return { matches: false, diffMs: wantDate.getTime() - gotDate.getTime() };
    };
    
    // Start with UTC timestamp having the same numeric values as target
    let utcMs = Date.UTC(tYear, tMonth - 1, tDay, tHour, tMinute, 0, 0);
    
    // Iterate up to 3 times to converge
    for (let i = 0; i < 3; i++) {
      const result = matchesTarget(utcMs);
      if (result.matches) {
        break;
      }
      utcMs += result.diffMs;
    }
    
    return new Date(utcMs);
  };

  const handleTimeChange = (time: string) => {
    setSelectedTime(time);
    if (time === 'asap') {
      onTimeSelect(selectedDate, true);
    } else {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const minute = parseInt(minutes);
      
      if (timezone && selectedDate) {
        try {
          // Calculate which day index this is (0 = today, 1 = tomorrow, etc.)
          // Use the dates array index based on matching the selectedDate
          const dayIndex = dates.findIndex(d => d.toDateString() === selectedDate.toDateString());
          
          if (dayIndex >= 0) {
            // Convert restaurant local time to UTC
            const utcDate = restaurantLocalToUtc(dayIndex, hour, minute, timezone);
            
            console.log(`[DateTimeSelector] Selected day ${dayIndex}, ${hour}:${String(minute).padStart(2, '0')} in ${timezone}`);
            console.log(`[DateTimeSelector] UTC timestamp: ${utcDate.getTime() / 1000}`);
            
            onTimeSelect(utcDate, false);
            return;
          }
        } catch (e) {
          console.warn('Failed to calculate restaurant timezone, falling back to local time', e);
        }
      }
      
      // Fallback: use local timezone if no restaurant timezone provided
      const dateTime = selectedDate ? new Date(selectedDate) : new Date();
      dateTime.setHours(hour, minute, 0, 0);
      onTimeSelect(dateTime, false);
    }
  };

  // Helper: Get current time in restaurant timezone (or local if no timezone)
  const getRestaurantTime = (tz: string | null | undefined) => {
    const now = new Date();
    if (!tz) {
      return {
        dayIndex: now.getDay(),
        currentMinutes: now.getHours() * 60 + now.getMinutes()
      };
    }
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        weekday: 'long',
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
      });
      const parts = formatter.formatToParts(now);
      const weekdayPart = parts.find(p => p.type === 'weekday')?.value?.toLowerCase();
      const hourPart = parts.find(p => p.type === 'hour')?.value;
      const minutePart = parts.find(p => p.type === 'minute')?.value;
      
      const dayIndex = dayNames.indexOf(weekdayPart || '');
      const hour = parseInt(hourPart || '0');
      const minute = parseInt(minutePart || '0');
      
      return {
        dayIndex: dayIndex !== -1 ? dayIndex : now.getDay(),
        currentMinutes: hour * 60 + minute
      };
    } catch {
      return {
        dayIndex: now.getDay(),
        currentMinutes: now.getHours() * 60 + now.getMinutes()
      };
    }
  };

  // Generate time slots filtered by restaurant operating hours
  const generateTimeSlots = useMemo(() => {
    const slots: Array<{ value: string; label: string }> = [];
    if (!selectedDate) return slots;
    
    // Get current time in restaurant's timezone for "today" comparison
    const restaurantTime = getRestaurantTime(timezone);
    const now = new Date();
    const isToday = selectedDate.toDateString() === now.toDateString();
    const dayName = dayNames[selectedDate.getDay()];
    
    // Get operating windows for the selected day and service type
    const serviceHours = hoursJson?.[serviceType] || hoursJson?.delivery || hoursJson?.takeout;
    const windows = serviceHours ? getTimeWindows(serviceHours, dayName) : [];
    
    // hoursJson is provided = we have real schedule data.
    // If windows is empty AND we have hoursJson, the day is genuinely closed — return no slots.
    // Only fall back to the 8am-10pm default when hoursJson is absent entirely.
    const hasHoursJson = !!hoursJson;
    const hasHoursData = windows.length > 0;
    
    // Day is closed (has schedule data but no windows for this day)
    if (hasHoursJson && !hasHoursData) {
      return slots; // empty — no time selection possible for this day
    }
    
    // Calculate minimum start time using restaurant timezone (current hour + 1 for today)
    const minStartMinutes = isToday ? (restaurantTime.currentMinutes + 60) : 0;
    
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const totalMinutes = hour * 60 + minute;
        
        // Skip times in the past for today (using restaurant timezone)
        if (totalMinutes < minStartMinutes) continue;
        
        // Check if within operating hours (if hours data is available)
        if (hasHoursData) {
          if (!isTimeInOperatingHours(totalMinutes, windows)) continue;
        } else {
          // Default fallback: 8am to 10pm (only reached when hoursJson is absent)
          if (hour < 8 || hour >= 22) continue;
        }
        
        const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const displayTime = new Date(0, 0, 0, hour, minute).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        slots.push({ value: timeStr, label: displayTime });
      }
    }
    
    return slots;
  }, [selectedDate, hoursJson, serviceType, dayNames, timezone]);

  const timeSlots = generateTimeSlots;

  // Determine which dates are closed (restaurant has hours data but no windows for that day)
  const isDateClosed = useMemo(() => {
    return dates.map(date => {
      if (!hoursJson) return false; // No hours data — never mark as closed
      const dn = dayNames[date.getDay()];
      const sh = (hoursJson as any)?.[serviceType] || (hoursJson as any)?.delivery || (hoursJson as any)?.takeout;
      const ws = sh ? getTimeWindows(sh, dn) : [];
      return ws.length === 0;
    });
  }, [dates, hoursJson, serviceType, dayNames]);

  // When selected date becomes closed (e.g. service type changed), auto-advance to next open date
  useEffect(() => {
    if (!selectedDate) return;
    const idx = dates.findIndex(d => d.toDateString() === selectedDate.toDateString());
    if (idx >= 0 && isDateClosed[idx]) {
      const nextOpen = dates.findIndex((_, i) => i > idx && !isDateClosed[i]);
      if (nextOpen >= 0) {
        setSelectedDate(dates[nextOpen]);
        setSelectedTime('');
      }
    }
  }, [isDateClosed]);

  const selectedDateIsClosed = selectedDate
    ? isDateClosed[dates.findIndex(d => d.toDateString() === selectedDate.toDateString())]
    : false;

  return (
    <div className={cn('space-y-4', className)}>
      <h3 className="text-2xl font-semibold">Order Time</h3>
      
      {/* Date Selector */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {dates.map((date, index) => {
          const isSelected = selectedDate?.toDateString() === date.toDateString();
          const isClosed = isDateClosed[index];
          
          return (
            <Button
              key={date.toISOString()}
              variant={isSelected ? 'default' : 'outline'}
              onClick={() => !isClosed && handleDateSelect(date)}
              disabled={isClosed}
              className={cn(
                'flex-shrink-0 flex flex-col items-center gap-1 px-6 py-3 h-auto min-w-[80px]',
                isSelected && !isClosed && 'bg-primary text-primary-foreground',
                isClosed && 'opacity-40 cursor-not-allowed'
              )}
              data-testid={`button-date-${index}`}
            >
              <span className="text-xs font-medium uppercase">
                {formatDayLabel(date, index)}
              </span>
              <span className="text-2xl font-bold">
                {date.getDate()}
              </span>
              {isClosed && (
                <span className="text-[9px] uppercase tracking-wide opacity-70">Closed</span>
              )}
            </Button>
          );
        })}
      </div>

      {/* Loading State Notice */}
      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
          <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin" />
          <span>Checking restaurant availability...</span>
        </div>
      )}

      {/* Closed day notice */}
      {selectedDateIsClosed && !isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
          <span>This restaurant is closed on the selected day. Please choose another date.</span>
        </div>
      )}

      {/* Time Selector — hidden when selected day is closed */}
      {selectedDate && !selectedDateIsClosed && (
        <Select value={selectedTime} onValueChange={handleTimeChange} disabled={isLoading}>
          <SelectTrigger className="h-auto py-4 text-lg" data-testid="select-time">
            <SelectValue placeholder="Select a time" />
          </SelectTrigger>
          <SelectContent>
            {isOpen && (
              <SelectItem value="asap">ASAP</SelectItem>
            )}
            {timeSlots.map((slot) => (
              <SelectItem key={slot.value} value={slot.value}>
                {slot.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {selectedDate && selectedTime !== 'asap' && !selectedDateIsClosed && (
        <p className="text-sm text-muted-foreground">
          Attention: Changing the date or time may remove the items in your cart
        </p>
      )}
    </div>
  );
}
